import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { callApi, TApiResponse } from '../api'
import {
	FieldTargetType,
	ICustomFieldsState,
	TAllCustomFieldsState,
	TBrandCustomField,
	TCustomFieldChannel,
	TCustomFieldsActions,
	TCustomFieldsMap,
	TRequestResponse,
	TTargetCustomFieldsInitialState,
	TTargetCustomFieldsState,
	TTargetDataLoadedState,
} from '../types'
import { CustomFields, prepareCustomFields, sortCustomFieldsByRank } from '../../utils/custom-fields'
import { profileHeaderToken } from '../utils'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialTargetFieldsState: TTargetCustomFieldsInitialState = {
	[FieldTargetType.members]: [],
	[FieldTargetType.experts]: [],
	[FieldTargetType.onboarding]: [],
}
const initialTargetFields: TTargetCustomFieldsState = {
	[FieldTargetType.members]: {
		fields: [],
		loaded: false,
	},
	[FieldTargetType.experts]: {
		fields: [],
		loaded: false,
	},
	[FieldTargetType.onboarding]: {
		fields: [],
		loaded: false,
	},
}

const initialTargetDataLoaded: TTargetDataLoadedState = {
	[FieldTargetType.members]: false,
	[FieldTargetType.experts]: false,
	[FieldTargetType.onboarding]: false,
}

const initialCustomFieldsState: ICustomFieldsState = {
	...initialTargetFields,
	channels: [],
	fields: {},
	allFields: {
		fields: [],
		loaded: false,
	},
	loading: false,
}

const CustomFieldsContext: Context<{
	customFields: ICustomFieldsState
	actions: TCustomFieldsActions
}> = createContext({
	customFields: initialCustomFieldsState,
	actions: {
		getCustomFields: () => {},
		getOnboardingCustomFields: (target: FieldTargetType[], referrer?: string) => {},
		clearCustomFields: () => {},
		overwriteCustomFields: (customFields: TBrandCustomField[]) => {},
		updateCustomFields: (customFields: CustomFields) => Promise.resolve({ ok: true } as TRequestResponse),
	},
})

export const CustomFieldsProvider = (props: PropsWithChildren<{}>) => {
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [fields, setFields] = useState<TCustomFieldsMap>({})
	const [channels, setChannels] = useState<TCustomFieldChannel[]>(initialCustomFieldsState.channels)
	const [targetFields, setTargetFields] = useState<TTargetCustomFieldsInitialState>(initialTargetFieldsState)
	const [targetDataLoaded, setTargetDataLoaded] = useState<TTargetDataLoadedState>(initialTargetDataLoaded)
	const [allFields, setAllFields] = useState<TBrandCustomField[]>(initialCustomFieldsState.allFields.fields)
	const [allFieldsLoaded, setAllFieldsLoaded] = useState<boolean>(initialCustomFieldsState.allFields.loaded)

	useEffect(() => {
		if (!allFieldsLoaded) {
			const allLoaded =
				targetDataLoaded[FieldTargetType.members] &&
				targetDataLoaded[FieldTargetType.experts] &&
				targetDataLoaded[FieldTargetType.onboarding]
			setAllFieldsLoaded(allLoaded)
		}
	}, [targetFields])

	const overwriteTargetFields = (customFieldsMap: { [id: string]: TBrandCustomField }) => {
		const newTargetFields = JSON.parse(JSON.stringify(initialTargetFieldsState))

		for (const id of Object.keys(customFieldsMap)) {
			if (customFieldsMap[id][FieldTargetType.members]) {
				newTargetFields[FieldTargetType.members].push(customFieldsMap[id])
			}
			if (customFieldsMap[id][FieldTargetType.experts]) {
				newTargetFields[FieldTargetType.experts].push(customFieldsMap[id])
			}
			if (customFieldsMap[id][FieldTargetType.onboarding]) {
				newTargetFields[FieldTargetType.onboarding].push(customFieldsMap[id])
			}
		}

		newTargetFields[FieldTargetType.members] = newTargetFields[FieldTargetType.members].sort(sortCustomFieldsByRank)
		newTargetFields[FieldTargetType.experts] = newTargetFields[FieldTargetType.experts].sort(sortCustomFieldsByRank)
		newTargetFields[FieldTargetType.onboarding] = newTargetFields[FieldTargetType.onboarding].sort(
			sortCustomFieldsByRank
		)
		setTargetFields(newTargetFields)
		setLoading(false)
	}

	const updateTargetDataLoaded = () => {
		if (!allFieldsLoaded) {
			const targets = Object.values(FieldTargetType)
			const newTargetDataLoaded = targets.reduce(
				(res, targetKey) => {
					return {
						...res,
						[targetKey]: true,
					}
				},
				{ ...targetDataLoaded }
			)
			setTargetDataLoaded(newTargetDataLoaded)
		}
	}

	const fetchCustomFields = async (token?: string) => {
		setLoading(true)
		const fieldsResponse: TApiResponse = await callApi('/custom-fields', {
			method: 'GET',
			...(token ? { headers: profileHeaderToken(token) } : {}),
		})

		const { data } = fieldsResponse.payload

		if (fieldsResponse.ok) {
			setProviderError(null)
			updateTargetDataLoaded()
			setChannels(data.channels)
			refreshCustomFields(data.fields)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Custom Fields!')
		}
	}

	const fetchOnboardingCustomFields = async (target: FieldTargetType | FieldTargetType[], referrer?: string) => {
		setLoading(true)
		const fieldsResponse: TApiResponse = await callApi('/custom-fields/onboarding', {
			method: 'GET',
			params: { referrer, target },
		})

		const { data } = fieldsResponse.payload

		if (fieldsResponse.ok) {
			setProviderError(null)
			setTargetDataLoaded((prevDataLoaded) => ({
				...prevDataLoaded,
				[FieldTargetType.onboarding]: true,
			}))
			refreshCustomFields(data.fields)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Custom Fields!')
		}
	}

	const fetchUpdateCustomFields = async (updatedFields: CustomFields) => {
		setLoading(true)
		const fieldsResponse: TApiResponse = await callApi('/custom-fields', {
			method: 'PUT',
			body: prepareCustomFields(updatedFields, allFields),
		})

		const { data } = fieldsResponse.payload

		if (fieldsResponse.ok) {
			setProviderError(null)
			refreshCustomFields(data.fields)

			return { ok: fieldsResponse.ok }
		} else {
			const error = data.message || 'Cannot update Custom Fields!'
			setLoading(false)
			setProviderError(error)

			return Promise.reject(error)
		}
	}

	const refreshCustomFields = (customFields: TBrandCustomField[], params?: FieldTargetType | FieldTargetType[]) => {
		const newFields = { ...fields }

		for (const field of customFields) {
			newFields[field.id] = field
		}

		setFields(newFields)
		setAllFields(Object.values(newFields).sort(sortCustomFieldsByRank))
		overwriteTargetFields(newFields)
	}

	const getCustomFields = useCallback(fetchCustomFields, [])
	const getOnboardingCustomFields = useCallback(fetchOnboardingCustomFields, [])
	const overwriteCustomFields = useCallback(refreshCustomFields, [fields])
	const updateCustomFields = useCallback(fetchUpdateCustomFields, [allFields])
	const clearCustomFields = useCallback(() => {
		setTargetDataLoaded(initialTargetDataLoaded)
		setTargetFields(initialTargetFieldsState)
		setFields({})
		setAllFields([])
		setAllFieldsLoaded(false)
		setProviderError(null)
		setLoading(false)
	}, [allFields, targetFields])

	const actions: TCustomFieldsActions = {
		getCustomFields,
		overwriteCustomFields,
		updateCustomFields,
		clearCustomFields,
		getOnboardingCustomFields,
	}

	const targetFieldsMap: TTargetCustomFieldsState = {
		[FieldTargetType.members]: {
			fields: targetFields[FieldTargetType.members],
			loaded: targetDataLoaded[FieldTargetType.members],
		},
		[FieldTargetType.experts]: {
			fields: targetFields[FieldTargetType.experts],
			loaded: targetDataLoaded[FieldTargetType.experts],
		},
		[FieldTargetType.onboarding]: {
			fields: targetFields[FieldTargetType.onboarding],
			loaded: targetDataLoaded[FieldTargetType.onboarding],
		},
	}

	const allFieldsMap: TAllCustomFieldsState = {
		allFields: {
			fields: allFields,
			loaded: allFieldsLoaded,
		},
	}

	return (
		<CustomFieldsContext.Provider
			value={{ customFields: { ...targetFieldsMap, ...allFieldsMap, fields, channels, loading, error }, actions }}
		>
			{props.children}
		</CustomFieldsContext.Provider>
	)
}

export const useCustomFieldsContext: () => {
	customFields: ICustomFieldsState
	actions: TCustomFieldsActions
} = () => {
	return useContext(CustomFieldsContext)
}
